import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api, { fmtOdds } from "../lib/api";
import { ChevronDown } from "lucide-react";

export function cashTier(prob) {
  if (prob >= 80) return { label: "Lock", cls: "bg-[#22c55e]/20 text-[#22c55e] border-[#22c55e]/50" };
  if (prob >= 70) return { label: "Pick", cls: "bg-[#a855f7]/15 text-[#a855f7] border-[#a855f7]/50" };
  return { label: "Coin Toss", cls: "bg-white/10 text-neutral-400 border-white/20" };
}

export function tierNote(prob) {
  const t = cashTier(prob);
  if (t.label === "Lock") return `Lock — the market prices this at ${prob}% to hit with the juice removed. Strong parlay anchor.`;
  if (t.label === "Pick") return `Pick — ${prob}% implied probability after devigging the books. Solid play, not bulletproof.`;
  return `Coin Toss — only ${prob}% implied probability. This one's closer to a flip; size your bet accordingly.`;
}

export function BetDetail({ b, testid }) {
  const items = [
    ["Game", b.game],
    ["When", [b.day_label, b.time_et].filter(Boolean).join(" · ") || null],
    ["Market", b.market || b.market_label],
    ["Best odds", b.odds != null ? `${fmtOdds(b.odds)}${b.book ? ` at ${b.book}` : ""}` : null],
    ["Probability", b.prob != null ? `${b.prob}% (devigged)` : null],
    ["Actual", b.actual != null ? `${b.actual}`.replace(/\.0$/, "") : null],
  ].filter(([, v]) => v != null);
  return (
    <div className="px-3.5 pb-3.5 cursor-default" data-testid={testid} onClick={(e) => e.stopPropagation()}>
      <div className="border-t border-white/10 pt-3 space-y-3">
        <div className="font-display font-bold uppercase tracking-wide text-sm leading-snug whitespace-normal">
          {b.player ? <>{b.player} <span className="text-[#a855f7]">{b.bet}</span></> : b.bet}
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-2">
          {items.map(([k, v]) => (
            <div key={k}>
              <div className="text-[9px] uppercase tracking-widest text-neutral-500 font-bold">{k}</div>
              <div className="font-data text-xs text-neutral-200 mt-0.5">{v}</div>
            </div>
          ))}
        </div>
        {b.prob != null && <p className="text-[11px] text-neutral-400 leading-relaxed">{tierNote(b.prob)}</p>}
      </div>
    </div>
  );
}

export default function PropsThatPay({ league, comp, standalone = false }) {
  const [open, setOpen] = useState(null);
  const [openG, setOpenG] = useState(null);
  const { data, isLoading } = useQuery({
    queryKey: [league, "propsBest", comp],
    queryFn: () => api.get(`/props/best?league=${league}${comp ? `&comp=${comp}` : ""}`).then((r) => r.data),
    staleTime: 30 * 60 * 1000,
  });
  const bets = data?.bets || [];
  const rec = data?.record;
  const graded = data?.graded || [];
  const fmtRec = (r) => `${r.w}-${r.l}${r.p ? `-${r.p}` : ""}`;
  const resBadge = (res) =>
    res === "win" ? "bg-[#22c55e]/20 text-[#22c55e] border-[#22c55e]/50"
      : res === "loss" ? "bg-red-500/15 text-red-400 border-red-500/40"
        : "bg-white/10 text-neutral-400 border-white/20";
  return (
    <div className="space-y-4">
      {standalone && (
        <h1 className="font-display text-3xl md:text-4xl font-black uppercase tracking-tight fade-up">Props That <span className="gold-text">Pay</span></h1>
      )}
      {rec && (
        <div className="card-gold border border-[#a855f7]/40 rounded-xl p-3.5 flex flex-wrap items-center gap-x-5 gap-y-2" data-testid="ptp-record">
          <div>
            <div className="text-[9px] uppercase tracking-widest text-neutral-500 font-bold">Props Record — {rec.date} <span className="text-neutral-600">· graded daily</span></div>
            <div className="font-data text-lg font-black gold-text">{fmtRec(rec)}{rec.w + rec.l > 0 ? ` (${Math.round((rec.w / (rec.w + rec.l)) * 100)}%)` : ""}</div>
          </div>
          <span className="text-[10px] text-neutral-600 ml-auto hidden sm:inline">Every listed prop auto-graded from final box scores</span>
        </div>
      )}
      <p className="text-neutral-500 text-sm">Only the specific props most likely to cash — scanned across every market and book, ranked by implied probability with the juice removed. <span className="text-[#22c55e] font-bold">Lock ≥80%</span> · <span className="text-[#a855f7] font-bold">Pick 70-79%</span> · everything else is a coin toss. Tap any prop to read it in full.</p>
      {isLoading && <div className="text-neutral-500 font-data text-sm animate-pulse">Scanning every prop menu for the cashers...</div>}
      {!isLoading && bets.length === 0 && <div className="card-surface rounded-xl p-10 text-center text-neutral-400" data-testid="ptp-empty">No props posted for this slate yet — books drop them closer to game time.</div>}
      <div className="space-y-2">
        {bets.map((b, i) => {
          const t = cashTier(b.prob);
          const isOpen = open === i;
          return (
            <div key={`${b.player}-${b.bet}-${b.game}`} role="button" aria-expanded={isOpen}
              onClick={() => setOpen(isOpen ? null : i)}
              className={`card-surface rounded-xl cursor-pointer transition-colors duration-200 hover:border-[#a855f7]/40 ${i === 0 ? "border border-[#22c55e]/40" : ""}`}
              data-testid={`ptp-row-${i}`}>
              <div className="p-3.5 flex items-center gap-3">
                <span className={`font-data text-sm font-black w-6 text-center shrink-0 ${i < 3 ? "text-[#22c55e]" : "text-neutral-600"}`}>{i + 1}</span>
                <div className="flex-1 min-w-0">
                  <div className="font-display font-bold uppercase tracking-wide text-sm truncate">{b.player} <span className="text-[#a855f7]">{b.bet}</span></div>
                  <div className="text-[11px] text-neutral-500 truncate">{b.game}{b.market ? ` · ${b.market}` : ""}</div>
                </div>
                <div className="hidden sm:block w-28 shrink-0">
                  <div className="h-1.5 bg-white/10 rounded-full overflow-hidden"><div className="h-full rounded-full bg-[#22c55e]" style={{ width: `${Math.min(b.prob, 100)}%` }} /></div>
                </div>
                <div className="text-right shrink-0">
                  <div className="font-data text-sm font-black text-[#22c55e]">{b.prob}%</div>
                  <div className="text-[10px] font-data text-neutral-500">{fmtOdds(b.odds)}{b.book ? ` · ${b.book}` : ""}</div>
                </div>
                <span className={`text-[8px] uppercase tracking-widest font-black px-2 py-1 rounded-full border shrink-0 ${t.cls}`}>{t.label}</span>
                <ChevronDown size={14} className={`text-neutral-500 shrink-0 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`} />
              </div>
              {isOpen && <BetDetail b={b} testid={`ptp-detail-${i}`} />}
            </div>
          );
        })}
      </div>
      {graded.length > 0 && (
        <div className="space-y-2" data-testid="ptp-graded">
          <h2 className="font-display text-lg font-bold uppercase tracking-wide text-[#a855f7] border-b border-[#a855f7]/20 pb-1 pt-2">Graded Props — {rec?.date}</h2>
          {graded.map((b, i) => {
            const isOpen = openG === i;
            return (
              <div key={`${b.player}-${b.bet}-${i}`} role="button" aria-expanded={isOpen}
                onClick={() => setOpenG(isOpen ? null : i)}
                className="card-surface rounded-xl cursor-pointer opacity-90 transition-colors duration-200 hover:border-[#a855f7]/40" data-testid={`ptp-graded-${i}`}>
                <div className="p-3 flex items-center gap-3">
                  <span className={`text-[9px] uppercase tracking-widest font-black px-2 py-1 rounded-full border shrink-0 w-14 text-center ${resBadge(b.result)}`}>
                    {b.result === "win" ? "WIN" : b.result === "loss" ? "LOSS" : "PUSH"}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="font-display font-bold uppercase tracking-wide text-sm truncate">{b.player} <span className="text-[#a855f7]">{b.bet}</span></div>
                    <div className="text-[11px] text-neutral-500 truncate">{b.game}{b.market_label ? ` · ${b.market_label}` : ""}</div>
                  </div>
                  {b.actual != null && (
                    <span className="text-[10px] font-data text-neutral-400 shrink-0">Actual: <b className="text-neutral-200">{typeof b.actual === "number" ? `${b.actual}`.replace(/\.0$/, "") : b.actual}</b></span>
                  )}
                  <div className="text-right shrink-0">
                    <div className="font-data text-xs font-black text-neutral-300">{b.prob}%</div>
                    <div className="text-[10px] font-data text-neutral-500">{fmtOdds(b.odds)}</div>
                  </div>
                  <ChevronDown size={14} className={`text-neutral-500 shrink-0 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`} />
                </div>
                {isOpen && <BetDetail b={b} testid={`ptp-graded-detail-${i}`} />}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
